import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { App as AntdApp, Alert, Descriptions, Space, Tag, Typography } from "antd";
import { Modal } from "../../../components/Common";
import { useMutations } from "../../../hooks/useApiMutation";
import RestoreOptions, { DEFAULT_RESTORE_OPTIONS, buildIdsPayload } from "./RestoreOptions";
import RestoreResult from "./RestoreResult";
import {
  BACKUPS_ENDPOINT,
  BACKUP_PROVIDER,
  BACKUP_PROVIDER_LABEL,
  RESTORE_MODE,
  formatBytes,
} from "../constants";

const { Text } = Typography;

/**
 * Restores an archive that lives on FTP or in the bucket rather than on this server.
 *
 * Opening the dialog fetches the file and reads its manifest, so the table list is what the
 * archive actually holds. The fetched copy is kept behind `stagingId` and handed back with the
 * restore, which is what keeps "look at it, then restore it" to a single transfer.
 */
const RemoteRestoreModal = ({ file, provider, ftpConnectionId, open, onClose, onDone }) => {
  const { mutate } = useMutations();
  // Stable handle for the load effect — see the note in BackupsPage.
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  const { modal } = AntdApp.useApp();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [running, setRunning] = useState(false);
  const [options, setOptions] = useState(DEFAULT_RESTORE_OPTIONS);
  const [result, setResult] = useState(null);

  const path = file?.path;

  const inspect = useCallback(async () => {
    setLoading(true);
    setFailed(false);

    await mutateRef.current("inspectRemoteBackup", {
      method: "post",
      endpoint: `${BACKUPS_ENDPOINT}/files/inspect`,
      payload: { provider, ftpConnectionId, path },
      onSuccess: setDetail,
      // The toast already carries the reason; this only has to stop the dialog from
      // offering a restore of something it could not read.
      onError: () => setFailed(true),
    });

    setLoading(false);
  }, [provider, ftpConnectionId, path]);

  useEffect(() => {
    if (!open || !path) return;

    setDetail(null);
    setResult(null);
    setFailed(false);
    setOptions(DEFAULT_RESTORE_OPTIONS);
    inspect();
  }, [open, path, inspect]);

  const tables = useMemo(
    () =>
      (detail?.tables || []).map((t) => ({
        name: t.name,
        rowCount: t.rowCount,
        supportsIdFilter: t.supportsIdFilter,
        disabled: !t.existsInTarget,
        disabledReason: "This table no longer exists in the live database.",
      })),
    [detail]
  );

  const selectedNames = useMemo(
    () =>
      options.mode === RESTORE_MODE.FULL
        ? tables.filter((t) => !t.disabled).map((t) => t.name)
        : options.tables || [],
    [options.mode, options.tables, tables]
  );

  const runRestore = async () => {
    setRunning(true);
    setResult(null);

    await mutate("restoreRemoteBackup", {
      method: "post",
      endpoint: `${BACKUPS_ENDPOINT}/files/restore`,
      payload: {
        provider,
        ftpConnectionId,
        path,
        stagingId: detail?.stagingId,
        mode: options.mode,
        strategy: options.strategy,
        tables: options.mode === RESTORE_MODE.SELECTIVE ? options.tables : undefined,
        ids: buildIdsPayload(options, selectedNames),
        skipSafetyBackup: options.skipSafetyBackup,
      },
      onSuccess: (data) => {
        setResult(data);
        onDone?.();
      },
      // A refused restore still rolls back a full result, which is worth showing.
      onError: (error) => {
        setResult(error?.response?.data?.data ?? error?.data ?? null);
        onDone?.();
      },
    });

    setRunning(false);
  };

  const confirmRestore = () => {
    if (!detail) {
      modal.warning({
        title: "Nothing to restore",
        content: "This file has not been read yet, or it could not be opened as a backup archive.",
      });
      return;
    }

    if (options.mode === RESTORE_MODE.SELECTIVE && selectedNames.length === 0) {
      modal.warning({ title: "Nothing selected", content: "Pick at least one table to restore." });
      return;
    }

    modal.confirm({
      title: "Restore this file?",
      centered: true,
      okText: "Restore",
      okType: "danger",
      content: (
        <span>
          {selectedNames.length} table{selectedNames.length === 1 ? "" : "s"} from{" "}
          <Text code>{file?.fileName}</Text> will be written into the live database. This cannot be
          undone except by restoring another backup.
        </span>
      ),
      onOk: runRestore,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={file ? `Restore ${file.fileName}` : "Restore backup file"}
      size="xl"
      confirmText={result ? "Close" : "Restore"}
      cancelText="Cancel"
      danger={!result}
      // The fetch has to finish before there is anything to restore, so the confirm button
      // spins along with it.
      loading={running || (!result && loading)}
      onConfirm={result ? onClose : confirmRestore}
    >
      {result ? (
        <RestoreResult result={result} />
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Alert
            type="info"
            showIcon
            message={`Fetched from ${BACKUP_PROVIDER_LABEL[provider]}`}
            description="The archive is pulled back onto this server before anything is written. Nothing on the remote side is changed or removed."
          />

          {failed && (
            <Alert
              type="error"
              showIcon
              message="This file could not be read"
              description="It was not reachable, or it is not a backup archive this server can open."
            />
          )}

          {detail && (
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Taken">
                {detail.createdAt ? new Date(detail.createdAt).toLocaleString() : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Size">{formatBytes(detail.sizeBytes)}</Descriptions.Item>
              <Descriptions.Item label="Source">
                <Text code>{detail.sourceServer || "—"}</Text> / {detail.sourceDatabase || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Contents">
                <Space size={6}>
                  <Tag>{detail.tables?.length ?? 0} tables</Tag>
                  <Tag>{(detail.rowCount ?? 0).toLocaleString()} rows</Tag>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label={provider === BACKUP_PROVIDER.S3 ? "Object key" : "Remote path"} span={2}>
                <Text code>{detail.path}</Text>
              </Descriptions.Item>
            </Descriptions>
          )}

          <RestoreOptions tables={tables} value={options} onChange={setOptions} loading={loading} />
        </Space>
      )}
    </Modal>
  );
};

export default RemoteRestoreModal;
